/**
 * snapshot.js
 *
 * Turns something drawn on the page (a boarding pass, a passport) into a PNG or PDF file, and saves files.
 * The drawing is rendered off screen at full size only while it's captured, like ShareablePass.
 */

import React, { useEffect, useRef, useState } from "react";
import { toBlob, toJpeg } from "html-to-image";
import { jsPDF } from "jspdf";
import JSZip from "jszip";

/**
 * Saves a file to the device
 * @param {Blob} blob
 * @param {String} filename     -   e.g. 'YJ030-souvenirs-Yash-Jain.zip'
 */
export const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
};

/**
 * Saves several files as one zip
 * @param {Array} files         -   [{ name, blob }]
 * @param {String} filename     -   The zip's name
 */
export const saveZip = async (files, filename) => {
    const zip = new JSZip();
    files.forEach(({ name, blob }) => zip.file(name, blob));
    saveBlob(await zip.generateAsync({ type: "blob" }), filename);
};

/**
 * @returns {Object}    -   { capture(node, format: 'png' | 'pdf', background) → Promise<Blob>, drawing (render it anywhere) }
 */
export const useSnapshot = () => {
    const [job, setJob] = useState(null);
    const ref = useRef(null);

    useEffect(() => {
        if (!job) return;
        const run = async () => {
            try {
                await document.fonts.ready;
                // Let images (the passport photo) finish decoding before drawing
                await Promise.all([...ref.current.querySelectorAll("img")].map((img) => img.decode?.().catch(() => {})));
                const { offsetWidth: width, offsetHeight: height } = ref.current;
                const options = { pixelRatio: 2, backgroundColor: job.background };
                if (job.format === "png") {
                    job.resolve(await toBlob(ref.current, options));
                } else {
                    const image = await toJpeg(ref.current, { ...options, quality: 0.92 });
                    const pdf = new jsPDF({ orientation: width > height ? "landscape" : "portrait", unit: "px", format: [width, height], hotfixes: ["px_scaling"] });
                    pdf.addImage(image, "JPEG", 0, 0, width, height);
                    job.resolve(pdf.output("blob"));
                }
            } catch (err) {
                job.reject(err);
            } finally {
                setJob(null);
            }
        };
        run();
    }, [job]);

    return {
        capture: (node, format, background = "#E9E4DA") => new Promise((resolve, reject) => setJob({ node, format, background, resolve, reject })),
        drawing: job && (
            <div style={{ position: "fixed", left: -20000, top: 0 }} aria-hidden="true">
                <div ref={ref} style={{ display: "inline-block" }}>{job.node}</div>
            </div>
        ),
    };
};
